import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { DividendsService } from './dividends.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-dividends',
    templateUrl: './dividends.component.html',
    styleUrls: ['./dividends.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DividendsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  private originalFormSnapshot: any = null;
  /** Callback already used to apply paid/received edit rules, so a late input does not refill the opposite side. */
  private appliedOpeningCallback = '';

  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  colData: any = {};
  modalName = '';
  modalDesc = '';

  title = 'Dividend Input';
  currentTabName = 'Form Entry';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  /** Display range for inline date error (MM/dd/yyyy), parity with AngularJS rowData.TAX_YEAR_BEGIN/END. */
  dateRangeBeginDisplay = '';
  dateRangeEndDisplay = '';
  dividendTypes: any[] = [];
  transCurrencies: any[] = [];
  payorTable: any[] = [];
  payeeTable: any[] = [];
  payorTotal = 0;
  payeeTotal = 0;
  selectedDividendType: any = null;
  selectedCurrency: any = null;
  paymentDate: string = '';
  payorAmount: any = 0;
  payorTaxAmount: any = 0;
  payeeAmount: any = 0;
  payeeTaxAmount: any = 0;
  cashDist = 'Y';
  payorEntityDisplay = '';
  payeeEntityDisplay = '';
  entity304Display = '';
  intlTransSearch = '';
  payorFeeds: any[] = [];
  payeeFeeds: any[] = [];
  payorEntities: any[] = [];
  payeeEntities: any[] = [];
  entity304Options: any[] = [];
  intlTransOptions: any[] = [];
  selectedPayorFeed: any = null;
  selectedPayeeFeed: any = null;
  selectedPayorEntity: any = null;
  selectedPayeeEntity: any = null;
  selectedEntity304: any = null;
  selectedIntlTrans: any = null;
  loadingPayorSearch = false;
  loadingPayeeSearch = false;
  loadingEntity304Search = false;
  loadingIntlTransSearch = false;
  activeSearchField: 'payor' | 'payee' | 'intlTrans' | 'entity304' | null = null;
  trans304Amount: any = 0;
  /** Mirrors AngularJS trans304Obj.TRANS_304_AMT_DISABLED until transactional currency is chosen (add) or from row (edit). */
  trans304AmountDisabled = true;
  /** Mirrors AngularJS trans304Obj.TARGET_ENTITY_304_DISABLED (edit + callback !== dividendAdjs keeps entity locked). */
  targetEntity304Disabled = true;
  taxBaseAmount: any = 0;
  divTypeInvalid = false;
  dateInvalid = false;

  /** USD-converted amounts (mirror legacy *_USD fields populated by convertToUS). */
  payorAmountUsd: any = 0;
  payorTaxAmountUsd: any = 0;
  payeeAmountUsd: any = 0;
  payeeTaxAmountUsd: any = 0;
  trans304AmountUsd: any = 0;
  taxBaseAmountUsd: any = 0;

  /** Per-field disabled flags (mirror legacy dividendPaidObj/dividendReceivedObj *_DISABLED). */
  payorDisabled = true;
  payorTbDisabled = true;
  payorAmtDisabled = true;
  payorTaxAmtDisabled = true;
  payeeDisabled = true;
  payeeTbDisabled = true;
  payeeAmtDisabled = true;
  payeeTaxAmtDisabled = true;

  /** Sign-validation flags (parity with $error.invalidAmt in legacy). */
  payorAmtInvalid = false;
  payorTaxAmtInvalid = false;
  payeeAmtInvalid = false;
  payeeTaxAmtInvalid = false;
  taxBaseAmtInvalid = false;
  tableMinimise = true;
  disableTable = false;
  tableTitle = 'Dividend Paid Details';
  detailRows: any[] = [];
  detailTotal = 0;
  detailAmountKey = 'PAID_TB_AMT';
  showConfirmDialog = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Delete Dividends',
    message: 'Are you sure you want to delete the dividend.',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  @Input('row-data')
  set _rowData(rowData: string) {
    try {
      const data = this.extractPlainData(this.parseInputValue(rowData));
      if (data && typeof data === 'object') {
        this.rowData = data;
        this.tryInit();
      }
    } catch (e) { /* row data input not yet stable */ }
  }

  @Input('col-data')
  set _colData(colData: string) {
    try {
      const data = this.extractPlainData(this.parseInputValue(colData));
      if (data && Object.keys(data).length > 0) {
        this.colData = data;
        if (this.isInitialized) {
          this.applyOpeningRulesIfNeeded();
          this.apply304FieldDisabledState();
          this.cdr.detectChanges();
        }
      }
      this.tryInit();
    } catch (e) { /* col data input not yet stable */ }
  }

  @Input('header-data')
  set _headerData(headerData: string) {
    try {
      const h = this.extractPlainData(this.parseInputValue(headerData));
      if (h && Object.keys(h).length > 0) {
        this.globalService = h;
        this.globalService.globalParams = this.extractPlainData(
          h.globalParams || h.globalparams || h.globalService?.globalParams || h.globalService || {}
        );
        this.USER_SETTINGS = this.extractPlainData(h.userSettingsData || {});
        this.GENERAL_CONFIG = this.extractPlainData(h.generalConfigData || {});
        this.tryInit();
      }
    } catch (e) { /* header data input not yet stable */ }
  }

  @Input('modal-name')
  set _modalName(modalName: string) {
    this.modalName = typeof modalName === 'string' ? modalName : '';
    if (this.isInitialized) {
      this.applyOpeningRulesIfNeeded();
      this.cdr.detectChanges();
    }
  }

  @Input('modal-desc')
  set _modalDesc(modalDesc: string) {
    this.modalDesc = typeof modalDesc === 'string' ? modalDesc : '';
    if (this.isInitialized) {
      this.applyOpeningRulesIfNeeded();
      this.cdr.detectChanges();
    }
  }

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private dividendsService: DividendsService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();

    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    this.globalService = header;
    this.globalService.filterParams = this.filterParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.globalService.filterParams = this.filterParams;

    const activeData = activeScreen?.data || {};
    const bridgedRow = activeData?.selected?.rowData ?? activeData?.rowData;
    if (bridgedRow && typeof bridgedRow === 'object' && Object.keys(bridgedRow).length > 0) {
      this.rowData = bridgedRow;
    }

    const bridgedCol = activeData?.colData;
    if (bridgedCol && typeof bridgedCol === 'object' && Object.keys(bridgedCol).length > 0 && !Object.keys(this.colData).length) {
      this.colData = bridgedCol;
    }

    const bridgedModal = activeData?.modal_name || activeData?.modalName || '';
    if (bridgedModal && !this.modalName) {
      this.modalName = String(bridgedModal);
    }
    const bridgedDesc = activeData?.modal_desc || activeData?.modalDesc || '';
    if (bridgedDesc && !this.modalDesc) {
      this.modalDesc = String(bridgedDesc);
    }

    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    if (!this.filterParams || Object.keys(this.filterParams).length === 0) return;
    if (!this.globalService || Object.keys(this.globalService).length === 0) return;
    if (!this.globalService.globalParams || Object.keys(this.globalService.globalParams).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url;
    if (!baseUrl) {
      this.showFormLoader = false;
      this.cdr.detectChanges();
      return;
    }
    this.showFormLoader = true;

    // Apply per-field disabled flags and seed feed list based on opening callback (parity with legacy init() linie 568-656).
    this.setupCallbackContext();

    this.dividendsService.getDividendTypes(baseUrl, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.dividendTypes = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
      if (this.rowData?.GROUP_TRANS_KEY) {
        this.selectedDividendType = this.dividendTypes.find((d: any) => String(d?.CODE_ID) === String(this.rowData?.DIVIDEND_TYPE_ID)) || this.selectedDividendType;
      }
      this.lockPresetSide();
      this.enableManualEntrySide();
      this.apply304FieldDisabledState();
      this.cdr.detectChanges();
    });
    this.dividendsService.getTransCurrencies(baseUrl, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.transCurrencies = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
      if (this.rowData?.GROUP_TRANS_KEY && !this.isPaidOpening) {
        this.selectedCurrency = this.transCurrencies.find((c: any) => String(c?.CURR_CODE) === String(this.rowData?.TRANS_304_CURR)) || this.selectedCurrency;
      }
      this.apply304FieldDisabledState();
      this.cdr.detectChanges();
    });
    // payorTable / payeeTable feed ONLY the detail table (parity with legacy: getDividendsPaid is used to compute payorTotal and detail rows, NOT the TB/Currency dropdown).
    this.dividendsService.getDividendsPaid(baseUrl, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.payorTable = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
      this.payorTotal = this.payorTable
        .filter((x: any) => this.matchesRowEntityKeys(x))
        .reduce((sum: number, x: any) => sum + (Number(x.PAID_TB_AMT) || 0), 0);
      this.setupDetailTable();
      this.cdr.detectChanges();
    });
    this.dividendsService.getDividendsReceived(baseUrl, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.payeeTable = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
        this.payeeTotal = this.payeeTable
          .filter((x: any) => this.matchesRowEntityKeys(x))
          .reduce((sum: number, x: any) => sum + (Number(x.RECEIVED_TB_AMT) || 0), 0);
        this.setupDetailTable();
        this.setupDisplayDefaults();
        this.hydrateFormForEdit();
        this.apply304FieldDisabledState();
        this.showFormLoader = false;
        this.captureOriginalFormSnapshot();
        this.cdr.detectChanges();
      },
      () => {
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Initial disabled-flags + feed-list state per opening callback.
   * Mirrors AngularJS DividendsController.init() lines 568-743 + populatePayorPayeeObjects() lines 815-878.
   * Paid and received openings keep their source entity/TB read-only even when the row is an existing dividend.
   */
  private setupCallbackContext(): void {
    this.resetFieldDisabledFlags();
    const side = this.formSide;
    const isEdit = !!this.rowData?.GROUP_TRANS_KEY;
    this.appliedOpeningCallback = side;

    if (side === 'paid') {
      this.applyPaidOpeningState();
      return;
    }

    if (side === 'received') {
      this.applyReceivedOpeningState();
      return;
    }

    if (isEdit) {
      // Generic dividend edit: both entity/TB stay locked, amounts stay editable.
      this.payorAmtDisabled = false;
      this.payorTaxAmtDisabled = false;
      this.payeeAmtDisabled = false;
      this.payeeTaxAmtDisabled = false;
    }
  }

  /**
   * col-data / modal-name can arrive after init. Re-apply paid or received rules once
   * so an edit opened from those columns does not keep the opposite side filled.
   */
  private applyOpeningRulesIfNeeded(): void {
    const side = this.formSide;
    if (side !== 'paid' && side !== 'received') {
      this.lockPresetSide();
      return;
    }
    if (this.appliedOpeningCallback === side) {
      this.lockPresetSide();
      this.enableManualEntrySide();
      return;
    }
    this.appliedOpeningCallback = side;
    if (side === 'paid') {
      this.applyPaidOpeningState();
    } else {
      this.applyReceivedOpeningState();
    }
    if (!this.showFormLoader) {
      this.captureOriginalFormSnapshot();
    }
  }

  /** Dividends Paid modal: payor entity and TB/currency stay read-only; received details stay blank. */
  private applyPaidOpeningState(): void {
    this.clearDividendReceivedFields();
    this.payorDisabled = true;
    this.payorTbDisabled = true;
    this.payorAmtDisabled = false;
    this.payorTaxAmtDisabled = false;
    const feed = this.buildFeedFromRowData(this.rowData);
    if (feed && !this.selectedPayorFeed) {
      this.payorFeeds = [feed];
      this.selectedPayorFeed = feed;
    }
    if (!this.payorEntityDisplay) {
      this.payorEntityDisplay = this.formatEntityFromRowData(this.rowData);
    }
    this.tableTitle = 'Dividend Paid Details';
    this.enableManualEntrySide();
  }

  /** Dividends Received modal: recipient entity and TB/currency stay read-only; paid details stay blank. */
  private applyReceivedOpeningState(): void {
    this.clearDividendPaidFields();
    this.payeeDisabled = true;
    this.payeeTbDisabled = true;
    this.payeeAmtDisabled = false;
    this.payeeTaxAmtDisabled = false;
    const feed = this.buildFeedFromRowData(this.rowData);
    if (feed && !this.selectedPayeeFeed) {
      this.payeeFeeds = [feed];
      this.selectedPayeeFeed = feed;
    }
    if (!this.payeeEntityDisplay) {
      this.payeeEntityDisplay = this.formatEntityFromRowData(this.rowData);
    }
    this.tableTitle = 'Dividend Received Detail';
    this.enableManualEntrySide();
  }

  private resetFieldDisabledFlags(): void {
    this.payorDisabled = true;
    this.payorTbDisabled = true;
    this.payorAmtDisabled = true;
    this.payorTaxAmtDisabled = true;
    this.payeeDisabled = true;
    this.payeeTbDisabled = true;
    this.payeeAmtDisabled = true;
    this.payeeTaxAmtDisabled = true;
  }

  /** Source-side entity and trial balance stay read-only for the column that opened the modal. */
  private lockPresetSide(): void {
    if (this.isPaidOpening) {
      this.payorDisabled = true;
      this.payorTbDisabled = true;
    }
    if (this.isReceivedOpening) {
      this.payeeDisabled = true;
      this.payeeTbDisabled = true;
    }
  }

  /** Opposite fieldset is open for manual entry as soon as the modal opens; no dividend type needed. */
  private enableManualEntrySide(): void {
    if (this.isPaidOpening) {
      this.payeeDisabled = false;
      this.payeeTbDisabled = false;
      this.payeeAmtDisabled = false;
      this.payeeTaxAmtDisabled = false;
    }
    if (this.isReceivedOpening) {
      this.payorDisabled = false;
      this.payorTbDisabled = false;
      this.payorAmtDisabled = false;
      this.payorTaxAmtDisabled = false;
    }
  }

  private clearDividendReceivedFields(): void {
    this.payeeEntityDisplay = '';
    this.payeeFeeds = [];
    this.selectedPayeeFeed = null;
    this.selectedPayeeEntity = null;
    this.payeeEntities = [];
    this.payeeAmount = 0;
    this.payeeTaxAmount = 0;
    this.payeeAmountUsd = 0;
    this.payeeTaxAmountUsd = 0;
    this.payeeAmtInvalid = false;
    this.payeeTaxAmtInvalid = false;
    this.payeeDisabled = true;
    this.payeeTbDisabled = true;
    this.payeeAmtDisabled = true;
    this.payeeTaxAmtDisabled = true;
    this.trans304Amount = 0;
    this.trans304AmountUsd = 0;
    this.entity304Display = '';
    this.selectedEntity304 = null;
    this.selectedCurrency = null;
  }

  private clearDividendPaidFields(): void {
    this.payorEntityDisplay = '';
    this.payorFeeds = [];
    this.selectedPayorFeed = null;
    this.selectedPayorEntity = null;
    this.payorEntities = [];
    this.payorAmount = 0;
    this.payorTaxAmount = 0;
    this.payorAmountUsd = 0;
    this.payorTaxAmountUsd = 0;
    this.payorAmtInvalid = false;
    this.payorTaxAmtInvalid = false;
    this.payorDisabled = true;
    this.payorTbDisabled = true;
    this.payorAmtDisabled = true;
    this.payorTaxAmtDisabled = true;
    this.cashDist = 'Y';
    this.taxBaseAmount = 0;
    this.taxBaseAmountUsd = 0;
    this.taxBaseAmtInvalid = false;
  }

  /** Build the single TB/Currency feed entry from rowData (parity with legacy init() linie 603-617, 633-647). */
  private buildFeedFromRowData(rowData: any): any | null {
    if (!rowData || !rowData.LEID) {
      return null;
    }
    return {
      CDR_NO: rowData.CDR_NO,
      COMBINATION_KEY: rowData.COMBINATION_KEY,
      CURR: rowData.CURR,
      FEED_DISPLAY: `${rowData.LEID}/${rowData.CDR_NO}/${rowData.ME_CODE}/${rowData.LEDGER}/${rowData.CURR}`,
      LEID: rowData.LEID,
      ME_CODE: rowData.ME_CODE,
      TAX_YEAR_BEGIN: rowData.TAX_YEAR_BEGIN,
      TAX_YEAR_END: rowData.TAX_YEAR_END,
      WTD_AVG_RATE: rowData.WTD_AVG_RATE,
    };
  }

  /** Match legacy entity display: `${LEID}/${CDR_NO}-${LE_NAME}(${REPORTING_PERIOD})`. */
  private formatEntityFromRowData(rowData: any): string {
    if (!rowData || !rowData.LEID) {
      return '';
    }
    const rp = rowData.REPORTING_PERIOD ? `(${rowData.REPORTING_PERIOD})` : '';
    return `${rowData.LEID}/${rowData.CDR_NO || ''}-${rowData.LE_NAME || ''}${rp}`;
  }

  save(): void {
    if (this.crudLoading || this.isSaveClicked) return;
    if (!this.validateBeforeSave()) {
      return;
    }
    this.crudLoading = true;
    this.isSaveClicked = true;
    this.cdr.detectChanges();
    const transDate = this.formatDateForSave(this.paymentDate);
    const transSubType = Number(this.selectedDividendType?.CODE_ID || 0);
    const recipientTransType = this.isNonTaxDividend(transSubType) ? 21 : 22;
    const intlTransKey = this.getIntlTransKey();
    const payorCombination = this.selectedPayorFeed?.COMBINATION_KEY || this.rowData?.PAYOR_COMBINATION_KEY || this.rowData?.COMBINATION_KEY || 0;
    const payeeCombination = this.selectedPayeeFeed?.COMBINATION_KEY || this.rowData?.PAYEE_COMBINATION_KEY || this.rowData?.COMBINATION_KEY || 0;
    const payorAdjSourceType = this.getAdjSourceType(this.selectedPayorFeed?.TAX_YEAR_BEGIN, this.selectedPayorFeed?.TAX_YEAR_END, this.paymentDate);
    const payeeAdjSourceType = this.getAdjSourceType(this.selectedPayeeFeed?.TAX_YEAR_BEGIN, this.selectedPayeeFeed?.TAX_YEAR_END, this.paymentDate);
    const headerSettings = [{
      tax_year: this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      GROUP_TRANS_KEY: this.rowData?.GROUP_TRANS_KEY || 0,
      ISSUE_KEY: this.getGlobalParam('issue_id') || null
    }];
    const transactions: any[] = [
      {
        TRANS_HEADER_KEY: this.rowData?.PAYOR_TRANS_HEADER_KEY || 0,
        COMBINATION_KEY: payorCombination,
        TRANS_TYPE_KEY: 20,
        INTL_TRANS_KEY: intlTransKey,
        ADJ_SOURCE_TYPE: payorAdjSourceType,
        TRANS_SUB_TYPE: transSubType,
        TRANS_DETAILS_KEY: this.rowData?.PAYOR_TRANS_DETAILS_KEY || 0,
        TRANS_DATE: transDate,
        DIV_AMOUNT: Number(this.payorAmount) || 0,
        TAX_AMT: Number(this.payorTaxAmount) || 0,
        TP_KEY: payeeCombination,
        ADJ_TYPE: '',
        IS_TRANS_304: 'N',
        TRANS_CURR: this.selectedPayorFeed?.CURR || this.selectedCurrency?.CURR_CODE || '',
        CASH_Y_N: this.cashDist
      },
      {
        TRANS_HEADER_KEY: this.rowData?.PAYEE_TRANS_HEADER_KEY || 0,
        COMBINATION_KEY: payeeCombination,
        TRANS_TYPE_KEY: recipientTransType,
        INTL_TRANS_KEY: intlTransKey,
        ADJ_SOURCE_TYPE: payeeAdjSourceType,
        TRANS_SUB_TYPE: transSubType,
        TRANS_DETAILS_KEY: this.rowData?.PAYEE_TRANS_DETAILS_KEY || 0,
        TRANS_DATE: transDate,
        DIV_AMOUNT: Number(this.payeeAmount) || 0,
        TAX_AMT: Number(this.payeeTaxAmount) || 0,
        TP_KEY: payorCombination,
        ADJ_TYPE: transSubType === 2 ? 'S' : '',
        IS_TRANS_304: 'N',
        TRANS_CURR: this.selectedPayeeFeed?.CURR || this.selectedCurrency?.CURR_CODE || '',
        CASH_Y_N: this.cashDist
      }
    ];

    if (transSubType === 2) {
      transactions.push({
        TRANS_HEADER_KEY: this.rowData?.TRANS_304_HEADER_KEY || 0,
        COMBINATION_KEY: this.selectedEntity304?.TP_KEY || this.rowData?.TRANS_304_TP_KEY || 0,
        TRANS_TYPE_KEY: 1010,
        INTL_TRANS_KEY: 0,
        ADJ_SOURCE_TYPE: '',
        TRANS_SUB_TYPE: transSubType,
        TRANS_DETAILS_KEY: this.rowData?.TRANS_304_DETAILS_KEY || 0,
        TRANS_DATE: transDate,
        DIV_AMOUNT: Number(this.trans304Amount) || Number(this.rowData?.TRANS_304_AMT) || 0,
        TAX_AMT: 0,
        TP_KEY: '',
        ADJ_TYPE: 'P',
        IS_TRANS_304: 'Y',
        TRANS_CURR: this.selectedCurrency?.CURR_CODE || this.rowData?.TRANS_304_CURR || '',
        CASH_Y_N: this.cashDist
      });
    }

    if (this.cashDist === 'N') {
      transactions.push({
        TRANS_HEADER_KEY: this.rowData?.TRANS_CASH_DIST_HEADER_KEY || 0,
        COMBINATION_KEY: payorCombination,
        TRANS_TYPE_KEY: 1177,
        INTL_TRANS_KEY: 0,
        ADJ_SOURCE_TYPE: '',
        TRANS_SUB_TYPE: transSubType,
        TRANS_DETAILS_KEY: this.rowData?.TRANS_CASH_DIST_DETAILS_KEY || 0,
        TRANS_DATE: transDate,
        DIV_AMOUNT: Number(this.taxBaseAmount) || Number(this.rowData?.TAX_BASE_VAL) || 0,
        TAX_AMT: 0,
        TP_KEY: '',
        ADJ_TYPE: '',
        IS_TRANS_304: 'N',
        TRANS_CURR: '',
        CASH_Y_N: this.cashDist
      });
    }
    this.dividendsService.saveUpdateDividends(
      this.GENERAL_CONFIG.base_url,
      transactions,
      headerSettings,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        if (res?.errorMessage && res.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(res.errorMessage, 'gtw-snackbar--red');
        } else {
          const message = Number(this.rowData?.GROUP_TRANS_KEY || 0) === 0
            ? 'Dividends have been successfully saved'
            : 'Dividends have been successfully updated';
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  deleteDividend(): void {
    if (!this.rowData?.GROUP_TRANS_KEY) {
      this.gtwSnackbarService.openSnackBar('No dividend record available to delete', 'gtw-snackbar--yellow');
      return;
    }
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirmDeleteDividend(): void {
    this.showConfirmDialog = false;
    this.performDeleteDividend();
  }

  onCancelDeleteDividend(): void {
    this.showConfirmDialog = false;
  }

  private performDeleteDividend(): void {
    if (!this.rowData?.GROUP_TRANS_KEY) {
      this.gtwSnackbarService.openSnackBar('No dividend record available to delete', 'gtw-snackbar--yellow');
      return;
    }
    this.crudLoading = true;
    this.cdr.detectChanges();
    const payload = { group_trans_key: this.rowData.GROUP_TRANS_KEY };
    this.dividendsService.deleteDividends(this.GENERAL_CONFIG.base_url, payload, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.crudLoading = false;
        if (res?.errorMessage && res.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(res.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Dividends have been successfully deleted', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while deleting. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  reset(): void {
    if (!this.originalFormSnapshot) {
      return;
    }
    const s = this.originalFormSnapshot;
    this.selectedDividendType = s.selectedDividendType;
    this.selectedCurrency = s.selectedCurrency;
    this.paymentDate = s.paymentDate;
    this.payorAmount = s.payorAmount;
    this.payorTaxAmount = s.payorTaxAmount;
    this.payeeAmount = s.payeeAmount;
    this.payeeTaxAmount = s.payeeTaxAmount;
    this.payorAmountUsd = s.payorAmountUsd;
    this.payorTaxAmountUsd = s.payorTaxAmountUsd;
    this.payeeAmountUsd = s.payeeAmountUsd;
    this.payeeTaxAmountUsd = s.payeeTaxAmountUsd;
    this.selectedPayorFeed = s.selectedPayorFeed;
    this.selectedPayeeFeed = s.selectedPayeeFeed;
    this.intlTransSearch = s.intlTransSearch;
    this.selectedIntlTrans = s.selectedIntlTrans;
    this.trans304Amount = s.trans304Amount;
    this.trans304AmountUsd = s.trans304AmountUsd;
    this.taxBaseAmount = s.taxBaseAmount;
    this.taxBaseAmountUsd = s.taxBaseAmountUsd;
    this.entity304Display = s.entity304Display;
    this.selectedEntity304 = s.selectedEntity304;
    this.cashDist = s.cashDist || 'Y';
    this.trans304AmountDisabled = s.trans304AmountDisabled;
    this.targetEntity304Disabled = s.targetEntity304Disabled;
    this.payorDisabled = s.payorDisabled;
    this.payorTbDisabled = s.payorTbDisabled;
    this.payorAmtDisabled = s.payorAmtDisabled;
    this.payorTaxAmtDisabled = s.payorTaxAmtDisabled;
    this.payeeDisabled = s.payeeDisabled;
    this.payeeTbDisabled = s.payeeTbDisabled;
    this.payeeAmtDisabled = s.payeeAmtDisabled;
    this.payeeTaxAmtDisabled = s.payeeTaxAmtDisabled;
    this.payorAmtInvalid = false;
    this.payorTaxAmtInvalid = false;
    this.payeeAmtInvalid = false;
    this.payeeTaxAmtInvalid = false;
    this.taxBaseAmtInvalid = false;
    this.divTypeInvalid = !this.selectedDividendType;
    this.validatePaymentDateRange();
    this.apply304FieldDisabledState();
    this.cdr.detectChanges();
  }

  private captureOriginalFormSnapshot(): void {
    this.originalFormSnapshot = {
      selectedDividendType: this.selectedDividendType,
      selectedCurrency: this.selectedCurrency,
      paymentDate: this.paymentDate,
      payorAmount: this.payorAmount,
      payorTaxAmount: this.payorTaxAmount,
      payeeAmount: this.payeeAmount,
      payeeTaxAmount: this.payeeTaxAmount,
      payorAmountUsd: this.payorAmountUsd,
      payorTaxAmountUsd: this.payorTaxAmountUsd,
      payeeAmountUsd: this.payeeAmountUsd,
      payeeTaxAmountUsd: this.payeeTaxAmountUsd,
      selectedPayorFeed: this.selectedPayorFeed,
      selectedPayeeFeed: this.selectedPayeeFeed,
      intlTransSearch: this.intlTransSearch,
      selectedIntlTrans: this.selectedIntlTrans,
      trans304Amount: this.trans304Amount,
      trans304AmountUsd: this.trans304AmountUsd,
      taxBaseAmount: this.taxBaseAmount,
      taxBaseAmountUsd: this.taxBaseAmountUsd,
      entity304Display: this.entity304Display,
      selectedEntity304: this.selectedEntity304,
      cashDist: this.cashDist,
      trans304AmountDisabled: this.trans304AmountDisabled,
      targetEntity304Disabled: this.targetEntity304Disabled,
      payorDisabled: this.payorDisabled,
      payorTbDisabled: this.payorTbDisabled,
      payorAmtDisabled: this.payorAmtDisabled,
      payorTaxAmtDisabled: this.payorTaxAmtDisabled,
      payeeDisabled: this.payeeDisabled,
      payeeTbDisabled: this.payeeTbDisabled,
      payeeAmtDisabled: this.payeeAmtDisabled,
      payeeTaxAmtDisabled: this.payeeTaxAmtDisabled
    };
  }

  private setupDisplayDefaults(): void {
    const leid = this.rowData?.LEID || '';
    const cdrNo = this.rowData?.CDR_NO || '';
    const leName = this.rowData?.LE_NAME || '';
    const rp = this.rowData?.REPORTING_PERIOD || '';
    const defaultEntity = leid
      ? `${leid}/${cdrNo}-${leName}${rp ? '(' + rp + ')' : ''}`
      : '';

    const isEdit = !!this.rowData?.GROUP_TRANS_KEY;

    if (this.isPaidOpening) {
      this.payorEntityDisplay = this.payorEntityDisplay || defaultEntity;
      return;
    }
    if (this.isReceivedOpening) {
      this.payeeEntityDisplay = this.payeeEntityDisplay || defaultEntity;
      return;
    }
    if (isEdit) {
      this.payorEntityDisplay = defaultEntity;
      this.payeeEntityDisplay = defaultEntity;
    }
  }

  toggleDetailTable(): void {
    this.tableMinimise = !this.tableMinimise;
  }

  private matchesRowEntityKeys(row: any): boolean {
    return String(row?.LEID ?? '') === String(this.rowData?.LEID ?? '')
      && String(row?.COMBINATION_KEY ?? '') === String(this.rowData?.COMBINATION_KEY ?? '');
  }

  private setupDetailTable(): void {
    const callback = this.openingCallback;
    const payorRows = this.payorTable.filter((x: any) => this.matchesRowEntityKeys(x));
    const payeeRows = this.payeeTable.filter((x: any) => this.matchesRowEntityKeys(x));

    if (callback === 'receiveddividendadjs') {
      this.tableTitle = 'Dividend Received Detail';
      this.detailRows = payeeRows;
      this.detailTotal = this.payeeTotal;
      this.detailAmountKey = 'RECEIVED_TB_AMT';
      this.disableTable = false;
      return;
    }

    if (callback === 'paiddividendadjs') {
      this.tableTitle = 'Dividend Paid Details';
      this.detailRows = payorRows;
      this.detailTotal = this.payorTotal;
      this.detailAmountKey = 'PAID_TB_AMT';
      this.disableTable = false;
      return;
    }

    if (payorRows.length > 0) {
      this.tableTitle = 'Dividend Paid Details';
      this.detailRows = payorRows;
      this.detailTotal = this.payorTotal;
      this.detailAmountKey = 'PAID_TB_AMT';
      this.disableTable = false;
      return;
    }

    if (payeeRows.length > 0) {
      this.tableTitle = 'Dividend Received Detail';
      this.detailRows = payeeRows;
      this.detailTotal = this.payeeTotal;
      this.detailAmountKey = 'RECEIVED_TB_AMT';
      this.disableTable = false;
      return;
    }

    this.disableTable = true;
  }

  /**
   * Mirrors AngularJS DividendsController.changeDivType() linie 100-144.
   * - newDividendAdd: type chosen → unlock both PAYOR and PAYEE entity searches.
   * - paidDividendAdjs: type chosen → unlock PAYEE entity (payor stays locked, was preset from row).
   * - receivedDividendAdjs: type chosen → unlock PAYOR entity (payee stays locked).
   * - For dividend type 2 (304) → set IS_TRANS_304='Y'; reset 304 fields when type changes away from 2.
   */
  onDividendTypeChange(value: any): void {
    this.selectedDividendType = value;
    this.divTypeInvalid = !this.selectedDividendType;
    const isEdit = !!this.rowData?.GROUP_TRANS_KEY;
    const isNewAdd = this.formSide === 'new';

    if (this.selectedDividendType) {
      if (isNewAdd) {
        this.payorDisabled = false;
        this.payeeDisabled = false;
      } else if (this.isPaidOpening) {
        // Paid modal: user enters Dividend Received. Payor entity/TB stay read-only.
        this.payeeDisabled = false;
      } else if (this.isReceivedOpening) {
        // Received modal: user enters Dividend Paid. Recipient entity/TB stay read-only.
        this.payorDisabled = false;
      } else if (!isEdit) {
        // legacy linia 130-131 else branch unlocks PAYOR entity for non-edit, non-paid callbacks.
        this.payorDisabled = false;
      }
    } else if (isNewAdd) {
      // legacy linia 141-142: cleared type in newAdd re-locks PAYOR.
      this.payorDisabled = true;
    }
    this.lockPresetSide();

    if (Number(this.selectedDividendType?.CODE_ID || 0) !== 2) {
      this.trans304Amount = 0;
      this.trans304AmountUsd = 0;
      this.entity304Display = '';
      this.selectedEntity304 = null;
      if (isNewAdd) {
        // legacy resets transCurrencies only on newAdd flow; preserve currency on edit (linie 803-812).
        this.selectedCurrency = null;
      }
    }
    this.apply304FieldDisabledState();
    this.cdr.detectChanges();
  }

  /**
   * Mirrors AngularJS DividendsController.changeTransCurr() linie 146-153.
   * Selecting transactional currency unlocks both 304 amount + target entity inputs and resets 304 amounts.
   */
  onTransCurrencyChange(cur: any): void {
    this.selectedCurrency = cur;
    this.trans304AmountDisabled = false;
    this.targetEntity304Disabled = false;
    if (this.isDividendType304() && !this.rowData?.GROUP_TRANS_KEY) {
      this.trans304Amount = 0;
      this.trans304AmountUsd = 0;
    }
    this.cdr.detectChanges();
  }

  /**
   * Mirrors AngularJS validateAmt() linie 283-307. Negative values rejected for payor/withholding/304/cashDist;
   * positive values rejected for payee_amt (recipient amount must be non-positive). Skipped when TRANS_SUB_TYPE=8.
   */
  validateAmt(val: any, wtdAvgRate: any, objType: 'PAIDOBJ' | 'RECEIVEDOBJ' | 'CASHDISTOBJ' | 'TRANS304OBJ', fieldToUpdate: string, fieldName: string): void {
    const numericVal = Number(val) || 0;
    const transSubType = Number(this.selectedDividendType?.CODE_ID || 0);
    if (fieldName !== 'trans_304_amt' && transSubType !== 8) {
      this.setAmtInvalidFlag(fieldName, false);
      if (fieldName === 'payee_amt' && numericVal > 0) {
        this.setAmtInvalidFlag(fieldName, true);
        return;
      }
      if (fieldName !== 'payee_amt' && numericVal < 0) {
        this.setAmtInvalidFlag(fieldName, true);
        return;
      }
    }
    this.convertToUS(numericVal, Number(wtdAvgRate) || 1, objType, fieldToUpdate);
    this.cdr.detectChanges();
  }

  private setAmtInvalidFlag(fieldName: string, invalid: boolean): void {
    if (fieldName === 'payor_amt') this.payorAmtInvalid = invalid;
    else if (fieldName === 'payor_tax_amt') this.payorTaxAmtInvalid = invalid;
    else if (fieldName === 'payee_amt') this.payeeAmtInvalid = invalid;
    else if (fieldName === 'payee_tax_amt') this.payeeTaxAmtInvalid = invalid;
    else if (fieldName === 'tax_base_val') this.taxBaseAmtInvalid = invalid;
  }

  /** Mirrors AngularJS convertToUS() linie 986-998: USD = Math.round(val * WTD_AVG_RATE) routed by objType+fieldToUpdate. */
  private convertToUS(val: number, rate: number, objType: string, fieldToUpdate: string): void {
    const usd = Math.round(val * rate);
    if (objType === 'PAIDOBJ' && fieldToUpdate === 'PAYOR_AMT_USD') this.payorAmountUsd = usd;
    else if (objType === 'PAIDOBJ' && fieldToUpdate === 'PAYOR_TAX_AMT_USD') this.payorTaxAmountUsd = usd;
    else if (objType === 'RECEIVEDOBJ' && fieldToUpdate === 'PAYEE_AMT_USD') this.payeeAmountUsd = usd;
    else if (objType === 'RECEIVEDOBJ' && fieldToUpdate === 'PAYEE_TAX_AMT_USD') this.payeeTaxAmountUsd = usd;
    else if (objType === 'CASHDISTOBJ' && fieldToUpdate === 'TAX_BASE_VAL_USD') this.taxBaseAmountUsd = usd;
    else if (objType === 'TRANS304OBJ' && fieldToUpdate === 'TRANS_304_AMT_USD') this.trans304AmountUsd = usd;
  }

  private isDividendType304(): boolean {
    return Number(this.selectedDividendType?.CODE_ID) === 2;
  }

  /** Parity with AngularJS trans304Obj disabled flags and changeTransCurr(). */
  private apply304FieldDisabledState(): void {
    if (!this.isDividendType304()) {
      this.trans304AmountDisabled = true;
      this.targetEntity304Disabled = true;
      return;
    }
    const editing = !!this.rowData?.GROUP_TRANS_KEY;
    if (editing) {
      this.trans304AmountDisabled = false;
      const cb = this.openingCallback;
      // Paid modal leaves the received fieldset blank, so the 304 target entity must be enterable.
      this.targetEntity304Disabled = cb !== 'dividendadjs' && !this.isPaidOpening;
      return;
    }
    const hasTransCurr = !!(this.selectedCurrency && this.selectedCurrency.CURR_CODE);
    this.trans304AmountDisabled = !hasTransCurr;
    this.targetEntity304Disabled = !hasTransCurr;
  }

  /** Mirrors AngularJS changePayor() linie 234-257. */
  onPayorFeedChange(value: any): void {
    if (this.payorTbDisabled || this.isPaidOpening) {
      this.lockPresetSide();
      this.cdr.detectChanges();
      return;
    }
    this.selectedPayorFeed = value;
    if (!value) {
      this.onPaymentDateChange(this.paymentDate);
      return;
    }
    this.payorAmtDisabled = false;
    this.payorTaxAmtDisabled = false;
    if (!this.isReceivedOpening && !this.isPaidOpening) {
      this.payeeDisabled = false;
    }
    this.lockPresetSide();
    this.payorAmount = 0;
    this.payorTaxAmount = 0;
    this.payorAmountUsd = 0;
    this.payorTaxAmountUsd = 0;
    this.payorAmtInvalid = false;
    this.payorTaxAmtInvalid = false;
    // newDividendAdd: date range comes from the selected payor feed (legacy formatDate).
    this.onPaymentDateChange(this.paymentDate);
    this.cdr.detectChanges();
  }

  /**
   * Mirrors AngularJS formatDate() linie 53-97.
   * Flags dateInvalid when the selected calendar day is outside tax-year begin/end.
   */
  onPaymentDateChange(value: string): void {
    this.paymentDate = value || '';
    this.validatePaymentDateRange();
    this.cdr.detectChanges();
  }

  /** Mirrors AngularJS changePayee() linie 259-281. */
  onPayeeFeedChange(value: any): void {
    if (this.payeeTbDisabled || this.isReceivedOpening) {
      this.lockPresetSide();
      this.cdr.detectChanges();
      return;
    }
    this.selectedPayeeFeed = value;
    if (!value) {
      return;
    }
    this.payeeAmtDisabled = false;
    this.payeeTaxAmtDisabled = false;
    this.payeeAmount = 0;
    this.payeeTaxAmount = 0;
    this.payeeAmountUsd = 0;
    this.payeeTaxAmountUsd = 0;
    this.payeeAmtInvalid = false;
    this.payeeTaxAmtInvalid = false;
    this.lockPresetSide();
    this.cdr.detectChanges();
  }

  onPayorSearchChange(search: string): void {
    if (this.payorDisabled || this.isPaidOpening) {
      this.cdr.detectChanges();
      return;
    }
    this.payorEntityDisplay = search;
    this.activeSearchField = 'payor';
    if (!search || search.length < 3) {
      this.payorEntities = [];
      this.loadingPayorSearch = false;
      return;
    }
    this.loadingPayorSearch = true;
    this.dividendsService.getEntities(
      this.GENERAL_CONFIG.base_url,
      this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      this.filterParams?.scenario || this.rowData?.SCENARIO,
      this.filterParams?.jcd_key || this.rowData?.JCD_KEY,
      search,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.payorEntities = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
      this.loadingPayorSearch = false;
      this.cdr.detectChanges();
    }, () => {
      this.loadingPayorSearch = false;
      this.payorEntities = [];
      this.cdr.detectChanges();
    });
  }

  onPayeeSearchChange(search: string): void {
    if (this.payeeDisabled || this.isReceivedOpening) {
      this.cdr.detectChanges();
      return;
    }
    this.payeeEntityDisplay = search;
    this.activeSearchField = 'payee';
    if (!search || search.length < 3) {
      this.payeeEntities = [];
      this.loadingPayeeSearch = false;
      return;
    }
    this.loadingPayeeSearch = true;
    this.dividendsService.getPayeeEntities(
      this.GENERAL_CONFIG.base_url,
      this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      this.filterParams?.scenario || this.rowData?.SCENARIO,
      this.filterParams?.jcd_key || this.rowData?.JCD_KEY,
      search,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.payeeEntities = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
      this.loadingPayeeSearch = false;
      this.cdr.detectChanges();
    }, () => {
      this.loadingPayeeSearch = false;
      this.payeeEntities = [];
      this.cdr.detectChanges();
    });
  }

  onEntity304SearchChange(search: string): void {
    this.entity304Display = search;
    this.activeSearchField = 'entity304';
    if (!search || search.length < 3) {
      this.entity304Options = [];
      this.selectedEntity304 = null;
      this.loadingEntity304Search = false;
      return;
    }
    this.loadingEntity304Search = true;
    this.dividendsService.get304Entities(
      this.GENERAL_CONFIG.base_url,
      this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      this.filterParams?.scenario || this.rowData?.SCENARIO,
      this.filterParams?.jcd_key || this.rowData?.JCD_KEY,
      search,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.entity304Options = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
      this.loadingEntity304Search = false;
      this.cdr.detectChanges();
    }, () => {
      this.loadingEntity304Search = false;
      this.entity304Options = [];
      this.cdr.detectChanges();
    });
  }

  onIntlTransSearchChange(search: string): void {
    this.intlTransSearch = search;
    this.activeSearchField = 'intlTrans';
    if (!search || search.length < 2) {
      this.intlTransOptions = [];
      this.selectedIntlTrans = null;
      this.loadingIntlTransSearch = false;
      return;
    }
    this.loadingIntlTransSearch = true;
    this.dividendsService.getTransId(
      this.GENERAL_CONFIG.base_url,
      this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      search,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.intlTransOptions = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
      this.loadingIntlTransSearch = false;
      this.cdr.detectChanges();
    }, () => {
      this.loadingIntlTransSearch = false;
      this.intlTransOptions = [];
      this.cdr.detectChanges();
    });
  }

  /** Mirrors AngularJS setEntity() linie 192-222. Selecting payor entity resets amount fields and unlocks TB dropdown. */
  selectPayorEntity(entity: any): void {
    this.selectedPayorEntity = entity || null;
    this.payorEntityDisplay = entity?.DISPLAY_VALUE || entity?.LE_NAME || entity?.LEID || '';
    this.payorEntities = [];
    this.activeSearchField = null;
    this.payorAmount = 0;
    this.payorTaxAmount = 0;
    this.payorAmountUsd = 0;
    this.payorTaxAmountUsd = 0;
    this.loadEntityFeeds('PAYOR', entity?.TP_KEY);
    this.cdr.detectChanges();
  }

  /** Mirrors AngularJS setEntity() linie 192-222 for PAYEE branch. */
  selectPayeeEntity(entity: any): void {
    this.selectedPayeeEntity = entity || null;
    this.payeeEntityDisplay = entity?.DISPLAY_VALUE || entity?.LE_NAME || entity?.LEID || '';
    this.payeeEntities = [];
    this.activeSearchField = null;
    this.payeeAmount = 0;
    this.payeeTaxAmount = 0;
    this.payeeAmountUsd = 0;
    this.payeeTaxAmountUsd = 0;
    this.loadEntityFeeds('PAYEE', entity?.TP_KEY);
    this.cdr.detectChanges();
  }

  selectEntity304(entity: any): void {
    this.selectedEntity304 = entity || null;
    this.entity304Display = entity?.DISPLAY_VALUE || entity?.LE_NAME || entity?.LEID || '';
    this.entity304Options = [];
    this.activeSearchField = null;
    this.cdr.detectChanges();
  }

  selectIntlTrans(trans: any): void {
    this.selectedIntlTrans = trans || null;
    this.intlTransSearch = trans?.DISPLAY_VALUE || trans?.ENTRY_ID || '';
    this.intlTransOptions = [];
    this.activeSearchField = null;
    this.cdr.detectChanges();
  }

  private loadEntityFeeds(entityType: 'PAYOR' | 'PAYEE', tpKey: any): void {
    // The getEntityFeeds / getPayeeEntityFeeds endpoints already return rows with FEED_DISPLAY built server-side
    // (parity with legacy DividendsController.js getEntityFeeds() linie 906-953 - direct assignment vm.payorFeeds.list = res).
    if (entityType === 'PAYOR') {
      this.dividendsService.getEntityFeeds(this.GENERAL_CONFIG.base_url, tpKey, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
        this.payorFeeds = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
        this.selectedPayorFeed = null;
        this.payorTbDisabled = false;
        this.lockPresetSide();
        this.cdr.detectChanges();
      });
      return;
    }
    this.dividendsService.getPayeeEntityFeeds(this.GENERAL_CONFIG.base_url, tpKey, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.payeeFeeds = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
      this.selectedPayeeFeed = null;
      this.payeeTbDisabled = false;
      this.lockPresetSide();
      this.cdr.detectChanges();
    });
  }

  private validateBeforeSave(): boolean {
    this.divTypeInvalid = false;

    if (!this.selectedDividendType) {
      this.divTypeInvalid = true;
      this.cdr.detectChanges();
      return false;
    }
    if (!this.selectedPayorFeed) {
      this.gtwSnackbarService.openSnackBar('Please enter Payor feeds', 'gtw-snackbar--red');
      return false;
    }
    if (!this.selectedPayeeFeed) {
      this.gtwSnackbarService.openSnackBar('Please enter Payee feeds', 'gtw-snackbar--red');
      return false;
    }
    this.validatePaymentDateRange();
    if (!this.paymentDate || this.dateInvalid) {
      this.dateInvalid = true;
      this.gtwSnackbarService.openSnackBar('Please enter valid date', 'gtw-snackbar--red');
      this.cdr.detectChanges();
      return false;
    }
    // Sign rules per legacy validateAmt() linie 295-303: payor/payor-tax/304/tax-base must be >= 0,
    // payee_amt must be <= 0 (recipient amount is recorded as negative). Skipped when TRANS_SUB_TYPE === 8.
    if (Number(this.selectedDividendType?.CODE_ID || 0) !== 8) {
      if (Number(this.payorAmount) < 0) {
        this.gtwSnackbarService.openSnackBar('Please enter valid Payor amount (non-negative)', 'gtw-snackbar--red');
        return false;
      }
      if (Number(this.payeeAmount) > 0) {
        this.gtwSnackbarService.openSnackBar('Please enter valid Payee amount (must be negative)', 'gtw-snackbar--red');
        return false;
      }
    }
    if (this.payorTaxAmount === null || this.payorTaxAmount === undefined || Number.isNaN(Number(this.payorTaxAmount))) {
      this.gtwSnackbarService.openSnackBar('Please enter numeric value in Payor Withholding Tax', 'gtw-snackbar--red');
      return false;
    }
    if (this.payeeTaxAmount === null || this.payeeTaxAmount === undefined || Number.isNaN(Number(this.payeeTaxAmount))) {
      this.gtwSnackbarService.openSnackBar('Please enter valid numeric value in Payee Withholding Tax', 'gtw-snackbar--red');
      return false;
    }
    if (this.isDividendType304()) {
      if (!this.selectedCurrency?.CURR_CODE) {
        this.gtwSnackbarService.openSnackBar('Please select transactional currency', 'gtw-snackbar--yellow');
        return false;
      }
      const tpKey = this.selectedEntity304?.TP_KEY ?? this.rowData?.TRANS_304_TP_KEY;
      if (!this.targetEntity304Disabled && (!tpKey || Number(tpKey) === 0)) {
        this.gtwSnackbarService.openSnackBar('Please select Target Entity for 304', 'gtw-snackbar--red');
        return false;
      }
    }
    return true;
  }

  /**
   * Parity with AngularJS formatDate(): newDividendAdd uses payor feed begin/end;
   * other modes use rowData.TAX_YEAR_BEGIN/END. No feed yet on new add → do not flag.
   */
  private validatePaymentDateRange(): void {
    this.dateInvalid = false;
    this.dateRangeBeginDisplay = '';
    this.dateRangeEndDisplay = '';

    if (!this.paymentDate) {
      return;
    }

    const paymentKey = this.toLocalDateKey(this.paymentDate);
    if (!paymentKey) {
      this.dateInvalid = true;
      return;
    }

    let beginRaw: any;
    let endRaw: any;
    if (this.isNewDividendAdd) {
      if (!this.selectedPayorFeed) {
        return;
      }
      beginRaw = this.selectedPayorFeed.TAX_YEAR_BEGIN;
      endRaw = this.selectedPayorFeed.TAX_YEAR_END;
    } else {
      beginRaw = this.rowData?.TAX_YEAR_BEGIN;
      endRaw = this.rowData?.TAX_YEAR_END;
    }

    const beginKey = this.toLocalDateKey(beginRaw);
    const endKey = this.toLocalDateKey(endRaw);
    if (!beginKey || !endKey) {
      return;
    }

    this.dateRangeBeginDisplay = this.formatDateKeyDisplay(beginKey);
    this.dateRangeEndDisplay = this.formatDateKeyDisplay(endKey);
    if (paymentKey < beginKey || paymentKey > endKey) {
      this.dateInvalid = true;
    }
  }

  /** Format HTML date (YYYY-MM-DD) or Date/string as MM/dd/yyyy without UTC shift. */
  private formatDateForSave(dateValue: string): string {
    if (!dateValue) {
      return '';
    }
    const key = this.toLocalDateKey(dateValue);
    if (!key) {
      return dateValue;
    }
    return this.formatDateKeyDisplay(key);
  }

  private getAdjSourceType(beginDate: any, endDate: any, userDate: string): string {
    const userKey = this.toLocalDateKey(userDate);
    const beginKey = this.toLocalDateKey(beginDate || this.rowData?.TAX_YEAR_BEGIN);
    const endKey = this.toLocalDateKey(endDate || this.rowData?.TAX_YEAR_END);
    if (!userKey || !beginKey || !endKey) {
      return 'TB';
    }
    return userKey >= beginKey && userKey <= endKey ? 'TB' : 'RECLASS';
  }

  /** Normalize any date-like value to a local YYYY-MM-DD key for calendar comparisons. */
  private toLocalDateKey(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    if (typeof value === 'string') {
      const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
      }
      const usMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (usMatch) {
        const mm = usMatch[1].padStart(2, '0');
        const dd = usMatch[2].padStart(2, '0');
        return `${usMatch[3]}-${mm}-${dd}`;
      }
    }
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }

  private formatDateKeyDisplay(dateKey: string): string {
    const parts = (dateKey || '').split('-');
    if (parts.length !== 3) {
      return dateKey;
    }
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }

  private isNonTaxDividend(transSubType: number): boolean {
    const selected = (this.dividendTypes || []).find((x: any) => Number(x?.CODE_ID) === transSubType);
    return String(selected?.POST_DESC || '').toUpperCase() === 'NONTAX';
  }

  private getIntlTransKey(): number {
    if (this.selectedIntlTrans?.ENTRY_ID !== undefined && this.selectedIntlTrans?.ENTRY_ID !== null) {
      return Number(this.selectedIntlTrans.ENTRY_ID) || 0;
    }
    const parsed = Number((this.intlTransSearch || '').split('/')[0]);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Mirrors AngularJS populatePayorPayeeObjects() linie 815-878.
   * Hydrates form fields, USD-converted amounts, and pulls TB/Currency feed lists per side.
   */
  private hydrateFormForEdit(): void {
    if (!this.rowData?.GROUP_TRANS_KEY) {
      this.lockPresetSide();
      this.enableManualEntrySide();
      return;
    }
    this.paymentDate = this.toHtmlDate(this.rowData?.PAYMENT_DATE);
    if (this.rowData?.INTL_TRANS_KEY) {
      this.intlTransSearch = this.rowData?.TRANSACTION_NAME
        ? `${this.rowData.INTL_TRANS_KEY}/${this.rowData.TRANSACTION_NAME}`
        : String(this.rowData.INTL_TRANS_KEY);
    }

    // Dividends Received modal leaves Dividend Paid blank for manual entry.
    if (!this.isReceivedOpening) {
      this.payorEntityDisplay = this.rowData?.PAYOR_ENTITY_DTLS || this.payorEntityDisplay;
      this.cashDist = this.rowData?.DIV_CASH_DIST_YN || this.cashDist;
      this.payorAmount = this.rowData?.PAYOR_AMT ?? this.payorAmount;
      this.payorTaxAmount = this.rowData?.PAYOR_TAX_WITHOLD ?? this.payorTaxAmount;
      this.payorAmountUsd = this.rowData?.PAYOR_AMT_USD ?? 0;
      this.payorTaxAmountUsd = this.rowData?.PAYOR_TAX_WITHOLD_USD ?? 0;
      this.taxBaseAmount = this.rowData?.TAX_BASE_VAL ?? 0;
      this.taxBaseAmountUsd = this.rowData?.TAX_BASE_VAL_USD ?? 0;
      this.loadEntityFeedsForEdit('PAYOR');
    }

    // Dividends Paid modal leaves Dividend Received blank for manual entry.
    if (!this.isPaidOpening) {
      this.payeeEntityDisplay = this.rowData?.PAYEE_ENTITY_DTLS || this.payeeEntityDisplay;
      this.payeeAmount = this.rowData?.PAYEE_AMT ?? this.payeeAmount;
      this.payeeTaxAmount = this.rowData?.PAYEE_TAX_WITHOLD ?? this.payeeTaxAmount;
      this.payeeAmountUsd = this.rowData?.PAYEE_AMT_USD ?? 0;
      this.payeeTaxAmountUsd = this.rowData?.PAYEE_TAX_WITHOLD_USD ?? 0;
      this.trans304Amount = this.rowData?.TRANS_304_AMT ?? 0;
      this.trans304AmountUsd = this.rowData?.TRANS_304_AMT_USD ?? 0;
      this.entity304Display = this.rowData?.TRANS_304_ENTITY_DTLS || '';
      if (this.rowData?.TRANS_304_TP_KEY) {
        this.selectedEntity304 = {
          TP_KEY: this.rowData.TRANS_304_TP_KEY,
          DISPLAY_VALUE: this.rowData.TRANS_304_ENTITY_DTLS || ''
        };
      }
      this.loadEntityFeedsForEdit('PAYEE');
    }

    if (this.formSide === 'paid') {
      this.clearDividendReceivedFields();
      this.payorDisabled = true;
      this.payorTbDisabled = true;
      this.payorAmtDisabled = false;
      this.payorTaxAmtDisabled = false;
    } else if (this.formSide === 'received') {
      this.clearDividendPaidFields();
      this.payeeDisabled = true;
      this.payeeTbDisabled = true;
      this.payeeAmtDisabled = false;
      this.payeeTaxAmtDisabled = false;
    }
    this.lockPresetSide();
    this.enableManualEntrySide();
    this.validatePaymentDateRange();
  }

  /** Edit-mode fetch + auto-select by saved COMBINATION_KEY. */
  private loadEntityFeedsForEdit(side: 'PAYOR' | 'PAYEE'): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url;
    if (!baseUrl) return;
    if (side === 'PAYOR') {
      const tpKey = this.rowData?.PAYOR_TP_KEY;
      if (!tpKey) return;
      this.dividendsService.getEntityFeeds(baseUrl, tpKey, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
        if (this.isReceivedOpening) {
          return;
        }
        const list = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
        this.payorFeeds = list;
        this.selectedPayorFeed = list.find((f: any) => String(f?.COMBINATION_KEY) === String(this.rowData?.PAYOR_COMBINATION_KEY)) || this.selectedPayorFeed;
        this.lockPresetSide();
        this.cdr.detectChanges();
      });
      return;
    }
    const tpKey = this.rowData?.PAYEE_TP_KEY;
    if (!tpKey) return;
    this.dividendsService.getPayeeEntityFeeds(baseUrl, tpKey, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      if (this.isPaidOpening) {
        return;
      }
      const list = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
      this.payeeFeeds = list;
      this.selectedPayeeFeed = list.find((f: any) => String(f?.COMBINATION_KEY) === String(this.rowData?.PAYEE_COMBINATION_KEY)) || this.selectedPayeeFeed;
      this.lockPresetSide();
      this.cdr.detectChanges();
    });
  }

  /** Calendar picker only. Typed, pasted, and dropped dates are ignored. */
  blockManualDateEntry(event: KeyboardEvent): void {
    if (event.altKey || event.metaKey || event.key === 'Tab' || event.key === 'Escape' || event.key === 'Enter') {
      return;
    }
    event.preventDefault();
  }

  private toHtmlDate(value: any): string {
    return this.toLocalDateKey(value);
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  get isNewDividendAdd(): boolean {
    const name = String(this.modalName || this.colData?.call_back || this.rowData?.call_back || '').toLowerCase();
    return name === 'newdividendadd';
  }

  private parseInputValue(value: any): any {
    if (typeof value === 'string') {
      return JSON.parse(value || '{}');
    }
    return value || {};
  }

  private extractPlainData(input: any): any {
    if (input === null || input === undefined) {
      return input;
    }
    if (Array.isArray(input)) {
      return input.map((item: any) => this.extractPlainData(item));
    }
    if (typeof input === 'object') {
      const output: any = {};
      Object.keys(input).forEach((key: string) => {
        if (key.startsWith('$$') || key.startsWith('$')) {
          return;
        }
        const value = input[key];
        if (typeof value === 'function') {
          return;
        }
        output[key] = this.extractPlainData(value);
      });
      return output;
    }
    return input;
  }

  private getGlobalParam(key: string): any {
    return this.globalService?.[key] ?? this.globalService?.globalParams?.[key];
  }

  /** Paid, received, or other modal callback from modal-name, column, or row. */
  private get openingCallback(): string {
    const candidates = [
      this.modalName,
      this.colData?.call_back,
      this.colData?.modal_name,
      this.colData?.modalName,
      this.rowData?.call_back,
      this.rowData?.CALL_BACK
    ].map((value: any) => String(value || '').toLowerCase());
    if (candidates.includes('paiddividendadjs')) {
      return 'paiddividendadjs';
    }
    if (candidates.includes('receiveddividendadjs')) {
      return 'receiveddividendadjs';
    }
    return candidates.find((value: string) => !!value) || '';
  }

  /**
   * Which half of the form is the source side.
   * Detail edit uses dividendAdjs, so the side also comes from the column text
   * or from which entity on the row matches the summary LEID.
   */
  private get formSide(): 'paid' | 'received' | 'both' | 'new' {
    const callback = this.openingCallback;
    if (callback === 'paiddividendadjs') {
      return 'paid';
    }
    if (callback === 'receiveddividendadjs') {
      return 'received';
    }
    if (callback === 'newdividendadd') {
      return 'new';
    }
    return this.sideFromContextHints()
      || this.sideFromEntityMatch()
      || (this.rowData?.GROUP_TRANS_KEY ? 'both' : 'new');
  }

  private sideFromContextHints(): 'paid' | 'received' | null {
    let blob = String(this.modalDesc || '');
    try {
      blob = `${blob} ${JSON.stringify(this.colData || {})}`;
    } catch {
      blob = `${blob}`;
    }
    const text = blob.toLowerCase();
    const paid = /dividends?\s+paid|paid\s+dividends?|paiddividend|sum of dividends paid/.test(text);
    const received = /dividends?\s+received|received\s+dividends?|receiveddividend|sum of dividends received/.test(text);
    if (paid && !received) {
      return 'paid';
    }
    if (received && !paid) {
      return 'received';
    }
    return null;
  }

  /** Summary-entity key sits on the payor for a paid detail row, and on the recipient for a received detail row. */
  private sideFromEntityMatch(): 'paid' | 'received' | null {
    const combo = String(this.rowData?.COMBINATION_KEY ?? '').trim();
    const payorCombo = String(this.rowData?.PAYOR_COMBINATION_KEY ?? '').trim();
    const payeeCombo = String(this.rowData?.PAYEE_COMBINATION_KEY ?? '').trim();
    if (combo && combo !== '0') {
      const payorHit = !!payorCombo && combo === payorCombo;
      const payeeHit = !!payeeCombo && combo === payeeCombo;
      if (payorHit && !payeeHit) {
        return 'paid';
      }
      if (payeeHit && !payorHit) {
        return 'received';
      }
    }
    const leid = String(this.rowData?.LEID ?? '').trim();
    if (!leid) {
      return null;
    }
    const payorMatch = this.entityDetailsMatch(this.rowData?.PAYOR_LEID || this.rowData?.PAYOR_ENTITY_DTLS, leid);
    const payeeMatch = this.entityDetailsMatch(this.rowData?.PAYEE_LEID || this.rowData?.PAYEE_ENTITY_DTLS, leid);
    if (payorMatch && !payeeMatch) {
      return 'paid';
    }
    if (payeeMatch && !payorMatch) {
      return 'received';
    }
    return null;
  }

  private entityDetailsMatch(details: any, leid: string): boolean {
    const text = String(details ?? '').trim();
    if (!text || !leid) {
      return false;
    }
    const lead = text.split('/')[0].split('-')[0].trim();
    return lead === leid || text === leid;
  }

  private get isPaidOpening(): boolean {
    return this.formSide === 'paid';
  }

  private get isReceivedOpening(): boolean {
    return this.formSide === 'received';
  }
}
